/**
 * The signed-in user (GET /me) as a query: null = anonymous. Mutations that change the session (sign-in, MFA, step-up,
 * sign-out) write the new value straight into the cache.
 */
import type { Capability, Me } from "@prop/contracts";
import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, get, setCsrfToken } from "./api";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: (n, err) => !(err instanceof ApiError && err.status > 0 && err.status < 500) && n < 2, staleTime: 15_000, refetchOnWindowFocus: true },
  },
});

export const ME_KEY = ["me"] as const;

async function fetchMe(): Promise<Me | null> {
  try {
    return await get<Me>("/me");
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      setCsrfToken(null);
      return null;
    }
    throw err;
  }
}

/** `enabled: false` skips the request (the landing of a browser that never signed in, ADR-0048); data stays undefined. */
export function useMe({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({ queryKey: ME_KEY, queryFn: fetchMe, staleTime: 30_000, enabled });
}

export function useSetMe() {
  const qc = useQueryClient();
  return (me: Me | null) => {
    if (me) setCsrfToken(me.csrfToken);
    qc.setQueryData(ME_KEY, me);
  };
}

export function can(me: Me | null | undefined, cap: Capability): boolean {
  return !!me?.capabilities.includes(cap);
}
